import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Drawer, StatusBadge, TextField } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  createAdminStaff,
  updateAdminStaffPermissions,
  updateAdminStaffStatus,
  useAdminStaff,
} from '../api/admin.api'
import type { StaffItem } from '../api/admin.api'
import {
  drawerStack,
  filters,
  header,
  idText,
  muted,
  page,
  panel,
  panelSubtitle,
  panelTitle,
  permissionToggle,
  permissionCheckbox,
  permissionTitle,
  permissionToggleText,
  permissionsGrid,
  staffActions,
  staffGrid,
  staffItem,
  staffList,
  subtitle,
  title,
  titleBlock,
  value,
} from './admin.css'

const ADMIN_PERMISSIONS = [
  'SALE_READ',
  'EVENT_READ',
  'RECEIVABLE_READ',
  'REVENUE_READ',
  'RECONCILE_READ',
  'RECONCILE_RUN',
  'PRODUCT_READ',
  'PRODUCT_WRITE',
  'PAYOUT_READ',
  'OPS_ALERT_READ',
  'OPS_ALERT_MANAGE',
  'STAFF_MANAGE',
  'USER_READ',
  'USER_MANAGE',
  'USER_PLAN_MANAGE',
  'ACQUIRER_READ',
  'ACQUIRER_MANAGE',
  'QA_TOOLS',
  'FINANCIAL_CACHE_READ',
  'FINANCIAL_CACHE_MANAGE',
] as const

const PERMISSION_LABELS: Record<(typeof ADMIN_PERMISSIONS)[number], string> = {
  SALE_READ: 'Vendas',
  EVENT_READ: 'Eventos',
  RECEIVABLE_READ: 'Financeiro',
  REVENUE_READ: 'Receita',
  RECONCILE_READ: 'Conciliação',
  RECONCILE_RUN: 'Rodar reconciliação',
  PRODUCT_READ: 'Produtos',
  PRODUCT_WRITE: 'Editar produtos',
  PAYOUT_READ: 'Recebimentos',
  OPS_ALERT_READ: 'Alertas',
  OPS_ALERT_MANAGE: 'Resolver alertas',
  STAFF_MANAGE: 'Equipe',
  USER_READ: 'Consultar usuários',
  USER_MANAGE: 'Gerenciar usuários e KYC',
  USER_PLAN_MANAGE: 'Alterar planos e taxas dos usuários',
  ACQUIRER_READ: 'Consultar adquirentes',
  ACQUIRER_MANAGE: 'Gerenciar adquirentes',
  QA_TOOLS: 'Ferramentas de QA',
  FINANCIAL_CACHE_READ: 'Consultar cache financeiro',
  FINANCIAL_CACHE_MANAGE: 'Alterar cache financeiro',
}

type NewStaffForm = {
  name: string
  email: string
  password: string
  isSuper: boolean
  permissions: string[]
}

type AccessDraft = {
  isSuper: boolean
  permissions: string[]
}

const initialForm: NewStaffForm = {
  name: '',
  email: '',
  password: '',
  isSuper: false,
  permissions: ['SALE_READ', 'RECEIVABLE_READ', 'REVENUE_READ'],
}

function togglePermission(permissions: string[], permission: string): string[] {
  return permissions.includes(permission)
    ? permissions.filter((item) => item !== permission)
    : [...permissions, permission]
}

function accessFromStaff(item: StaffItem): AccessDraft {
  return {
    isSuper: item.isSuper,
    permissions: item.permissions,
  }
}

export function AdminStaffScreen() {
  const [form, setForm] = useState<NewStaffForm>(initialForm)
  const [selectedStaff, setSelectedStaff] = useState<StaffItem | null>(null)
  const [draftAccess, setDraftAccess] = useState<AccessDraft>({ isSuper: false, permissions: [] })
  const staff = useAdminStaff()
  const queryClient = useQueryClient()
  const createMutation = useMutation({
    mutationFn: () => createAdminStaff(form),
    onSuccess: () => {
      toast.success('Usuário administrativo criado.')
      setForm(initialForm)
      void queryClient.invalidateQueries({ queryKey: ['admin-staff'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível criar o usuário',
          fallback: 'Confira os dados e tente novamente.',
        }),
      )
    },
  })
  const accessMutation = useMutation({
    mutationFn: ({ staffId, access }: { staffId: string; access: AccessDraft }) =>
      updateAdminStaffPermissions(staffId, access),
    onSuccess: () => {
      toast.success('Permissões atualizadas.')
      setSelectedStaff(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-staff'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível atualizar permissões',
          fallback: 'Revise os acessos e tente novamente.',
        }),
      )
    },
  })
  const statusMutation = useMutation({
    mutationFn: (item: StaffItem) =>
      updateAdminStaffStatus(item.id, item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: () => {
      toast.success('Status atualizado.')
      void queryClient.invalidateQueries({ queryKey: ['admin-staff'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível atualizar status',
          fallback: 'Atualize a lista e tente novamente.',
        }),
      )
    },
  })

  return (
    <section className={page} data-testid="staff-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Equipe</h1>
          <p className={subtitle}>Crie admins e ajuste acessos sem sair da operação.</p>
        </div>
      </div>

      <div className={staffGrid}>
        <Card padding="md">
          <div className={panel}>
            <div>
              <h2 className={panelTitle}>Novo admin</h2>
              <p className={panelSubtitle}>
                Crie o acesso inicial e depois refine permissões quando precisar.
              </p>
            </div>
            <div className={filters}>
              <TextField
                label="Nome"
                value={form.name}
                onChange={(event) => {
                  const next = event.currentTarget.value
                  setForm((current) => ({ ...current, name: next }))
                }}
              />
              <TextField
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(event) => {
                  const next = event.currentTarget.value
                  setForm((current) => ({ ...current, email: next }))
                }}
              />
              <TextField
                label="Senha inicial"
                type="password"
                value={form.password}
                onChange={(event) => {
                  const next = event.currentTarget.value
                  setForm((current) => ({ ...current, password: next }))
                }}
              />
            </div>
            <label className={permissionToggle}>
              <span className={permissionToggleText}>Super-admin</span>
              <input
                className={permissionCheckbox}
                type="checkbox"
                checked={form.isSuper}
                onChange={(event) => {
                  const next = event.currentTarget.checked
                  setForm((current) => ({ ...current, isSuper: next }))
                }}
              />
            </label>
            {!form.isSuper ? (
              <PermissionGrid
                permissions={form.permissions}
                onToggle={(permission) =>
                  setForm((current) => ({
                    ...current,
                    permissions: togglePermission(current.permissions, permission),
                  }))
                }
              />
            ) : null}
            <Button
              type="button"
              disabled={
                !form.name.trim() ||
                !form.email.trim() ||
                form.password.length < 8 ||
                createMutation.isPending
              }
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Criando...' : 'Criar usuário'}
            </Button>
          </div>
        </Card>

        <Card padding="md">
          <div className={panel}>
            <div>
              <h2 className={panelTitle}>Admins cadastrados</h2>
              <p className={panelSubtitle}>
                {staff.data?.items.length ?? 0} usuários administrativos
              </p>
            </div>
            <div className={staffList}>
              {(staff.data?.items ?? []).map((item) => (
                <div className={staffItem} key={item.id}>
                  <div>
                    <span className={value}>{item.name}</span>
                    <br />
                    <span className={idText}>{item.email}</span>
                    <br />
                    <span className={muted}>
                      {item.isSuper ? 'Super-admin' : `${item.permissions.length} permissões`}
                    </span>
                  </div>
                  <StatusBadge
                    label={item.status}
                    tone={item.status === 'ACTIVE' ? 'success' : 'closed'}
                  />
                  <div className={staffActions}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSelectedStaff(item)
                        setDraftAccess(accessFromStaff(item))
                      }}
                    >
                      Permissões
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => statusMutation.mutate(item)}
                    >
                      {item.status === 'ACTIVE' ? 'Inativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>
              ))}
              {!staff.isLoading && (staff.data?.items ?? []).length === 0 ? (
                <span className={muted}>Nenhum admin cadastrado.</span>
              ) : null}
            </div>
          </div>
        </Card>
      </div>

      <Drawer
        open={selectedStaff !== null}
        onClose={() => setSelectedStaff(null)}
        title={selectedStaff ? `Permissões de ${selectedStaff.name}` : 'Permissões'}
        description="Revise o perfil e os acessos administrativos antes de salvar."
        size="standard"
        variant="form"
        busy={accessMutation.isPending}
        dirty={
          selectedStaff
            ? draftAccess.isSuper !== accessFromStaff(selectedStaff).isSuper ||
              [...draftAccess.permissions].sort().join('|') !==
                [...accessFromStaff(selectedStaff).permissions].sort().join('|')
            : false
        }
        footer={
          selectedStaff ? (
            <>
              <Button
                variant="secondary"
                disabled={accessMutation.isPending}
                onClick={() => setSelectedStaff(null)}
              >
                Cancelar
              </Button>
              <Button
                disabled={accessMutation.isPending}
                onClick={() =>
                  accessMutation.mutate({
                    staffId: selectedStaff.id,
                    access: draftAccess,
                  })
                }
              >
                {accessMutation.isPending ? 'Salvando...' : 'Salvar permissões'}
              </Button>
            </>
          ) : undefined
        }
      >
        <div className={drawerStack}>
          {selectedStaff ? (
            <>
              <div className={panel}>
                <span className={idText}>{selectedStaff.email}</span>
                <label className={permissionToggle}>
                  <span className={permissionToggleText}>Super-admin</span>
                  <input
                    className={permissionCheckbox}
                    type="checkbox"
                    checked={draftAccess.isSuper}
                    onChange={(event) => {
                      const next = event.currentTarget.checked
                      setDraftAccess((current) => ({ ...current, isSuper: next }))
                    }}
                  />
                </label>
              </div>
              {!draftAccess.isSuper ? (
                <PermissionGrid
                  permissions={draftAccess.permissions}
                  onToggle={(permission) =>
                    setDraftAccess((current) => ({
                      ...current,
                      permissions: togglePermission(current.permissions, permission),
                    }))
                  }
                />
              ) : null}
            </>
          ) : null}
        </div>
      </Drawer>
    </section>
  )
}

function PermissionGrid({
  permissions,
  onToggle,
}: {
  permissions: string[]
  onToggle: (permission: string) => void
}) {
  return (
    <div className={permissionsGrid}>
      {ADMIN_PERMISSIONS.map((permission) => (
        <label className={permissionToggle} key={permission} title={permission}>
          <span className={permissionToggleText}>
            <span className={permissionTitle}>{PERMISSION_LABELS[permission]}</span>
          </span>
          <input
            className={permissionCheckbox}
            type="checkbox"
            checked={permissions.includes(permission)}
            onChange={() => onToggle(permission)}
          />
        </label>
      ))}
    </div>
  )
}
